/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxios } from '@/lib/axios';
import { requestBaseURLGCPExtOrInt } from '@/config/serviceAPI';
import { createQuery } from '@/utils/createReactQuery';
import { type AxiosInstance } from 'axios';
import { type Gcp360ImageOptions, type Gcp360ImageResponse } from '../interface';
import { type QueryClient } from '@tanstack/react-query';

const GCP_URL = `${requestBaseURLGCPExtOrInt}/sls/b2c/product-discovery-product-data/v1/product-360-images`;

const getGcp360Image = async (
  options: Gcp360ImageOptions,
  axiosInstance?: AxiosInstance
): Promise<Gcp360ImageResponse[]> => {
  const { country, skuIds } = options;
  const response = await getAxios(axiosInstance).get<Gcp360ImageResponse[]>(GCP_URL, {
    params: {
      country,
      skuIds,
    },
    withCredentials: false,
  });
  return response.data;
};

const { useData: use360ImageInfo, prefetch: prefetch360ImageInfo } = createQuery<
  Gcp360ImageResponse[],
  Gcp360ImageOptions
>('three-sixty-image', async (options, axiosInstance) =>
  getGcp360Image({ ...options }, axiosInstance)
);

export const useGetGcp360Image = (props: { country: string; skuIds: string; enabled: boolean }) => {
  const { country, skuIds, enabled } = props;
  const options = { country, skuIds, enabled };
  return use360ImageInfo(options);
};

export const prefetchGcp360Image = async (
  axiosInstance: AxiosInstance,
  queryClient: QueryClient,
  options: Gcp360ImageOptions
) => {
  return prefetch360ImageInfo(queryClient, options, axiosInstance);
};
